<script setup lang="ts">
/**
 * KOMPRES PDF — halaman induk alat gratis, tema v3.
 *
 * Aturan yang sama dengan /kompres-foto: seluruh salinan, FAQ, dan blok
 * JSON-LD dibawa PERSIS dari app/pages/kompres-pdf/index.vue, dan `path`
 * canonical tetap menunjuk alamat produksi '/kompres-pdf' selama v3 masih
 * prototipe. Lihat catatan lengkapnya di v3/kompres-foto/index.vue.
 */
import type { Faq } from '~/composables/useSeo'

/**
 * Layout `ringan`, bukan `default` — alasannya sama dengan /kompres-foto:
 * layout utama membungkus isinya dengan <UApp> sehingga setiap halaman
 * menanggung runtime Nuxt UI lewat layout, sementara halaman ini tidak memakai
 * satu pun perilaku library itu. Tampilan tetap tema v3 yang sama persis.
 * Lihat catatan lengkap di layouts/ringan.vue.
 */
definePageMeta({ layout: 'ringan' })

const situs = useSitusUrl()

const faq: Faq[] = [
  {
    tanya: 'Kenapa PDF saya sulit dikecilkan?',
    jawab:
      'Karena kemungkinan besar isinya bukan teks, melainkan gambar hasil pindai. Ukuran PDF hasil pindai ditentukan oleh resolusi dan jumlah halamannya. PDF yang dibuat langsung dari pengolah kata jauh lebih mudah dikecilkan karena isinya teks sungguhan.'
  },
  {
    tanya: 'Bolehkah mengompres PDF yang sudah dibubuhi e-meterai?',
    jawab:
      'Sebaiknya jangan. Membongkar dan menyusun ulang isi PDF berisiko merusak keabsahan meterai elektroniknya. Siapkan dokumen dalam ukuran yang sudah sesuai sebelum meterai dibubuhkan.'
  },
  {
    tanya: 'Apakah kualitas teks di dalam PDF ikut turun?',
    jawab:
      'Untuk PDF berisi teks sungguhan, teksnya tidak diubah — yang dirapikan adalah struktur berkas dan gambar di dalamnya. Untuk PDF hasil pindai, gambarnya memang dikompres, dan bila target terlalu ketat kami akan melaporkannya sebagai tidak tercapai daripada menghasilkan dokumen yang tidak terbaca.'
  },
  {
    tanya: 'Berapa ukuran berkas maksimal yang bisa diunggah?',
    jawab:
      'Satu berkas berukuran maksimal 15 MB per proses. Bila berkas kamu lebih besar dari itu, pindai ulang pada resolusi lebih rendah atau pisahkan menjadi beberapa berkas.'
  },
  {
    tanya: 'Apakah PDF saya disimpan?',
    jawab:
      'Tidak. Berkas diproses di memori dan hasilnya langsung dikembalikan. Tidak ada salinan yang tertinggal di server dan tidak ada akun yang perlu dibuat.'
  }
]

pakaiSeo({
  judul: 'Kompres PDF ke Ukuran KB Tertentu — Gratis, Tanpa Akun',
  deskripsi:
    'Perkecil berkas PDF ke 200 KB, 500 KB, 800 KB, atau angka bebas untuk ijazah, transkrip nilai, dan dokumen pendaftaran. Hasil langsung, berkas tidak disimpan.',
  path: '/kompres-pdf',
  jsonLd: [
    jsonLdAplikasi({
      nama: 'Kompres PDF ke Ukuran KB',
      deskripsi:
        'Alat daring gratis untuk menurunkan ukuran berkas PDF ke target kilobyte tertentu, dengan preset ukuran untuk berkas pendaftaran.',
      url: situs + '/kompres-pdf',
      harga: 0
    }),
    jsonLdFaq(faq),
    jsonLdRemah(situs, [
      ['Beranda', '/'],
      ['Kompres PDF', '/kompres-pdf']
    ])
  ]
})

const preset = [
  { ke: '/kompres-pdf/200kb', label: '200 KB', konteks: 'Batas ketat untuk dokumen ringkas' },
  { ke: '/kompres-pdf/500kb', label: '500 KB', konteks: 'Transkrip nilai & akta kelahiran' },
  { ke: '/kompres-pdf/800kb', label: '800 KB', konteks: 'Ijazah & dokumen syarat khusus' }
]
</script>

<template>
  <div>
    <!-- HERO + ALAT -->
    <section class="relative overflow-hidden">
      <div class="ca-aura" aria-hidden="true" />

      <div class="relative mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 pt-8 sm:pt-12 pb-10">
        <RinganRemah :jejak="[['Beranda', '/'], ['Kompres PDF', '']]" />

        <div class="mt-5 grid gap-9 lg:grid-cols-[1.05fr_1fr] lg:gap-12 lg:items-start">
          <div class="min-w-0">
            <RinganLencana tone="ok" icon="gift">Gratis selamanya</RinganLencana>

            <h1 class="ca-h1 mt-4 text-ink text-balance">
              Kompres PDF ke ukuran KB tertentu
            </h1>

            <p class="mt-4 text-lg text-muted leading-relaxed max-w-xl">
              Untuk ijazah, transkrip nilai, akta, dan berkas pendaftaran lain yang ditolak karena
              melebihi batas unggah. Satu berkas per proses, hasil langsung tanpa antrean, dan
              berkas tidak disimpan di server.
            </p>

          </div>

          <RinganKompresAlat jenis="pdf" :target-awal="500" />
        </div>

        <!-- Selebar halaman, bukan di kolom kiri — lihat catatan yang sama di
             v3/kompres-foto/index.vue. -->
        <div class="mt-10">
          <RinganBilahPercaya
            :butir="[
              { ikon: 'user-x', judul: 'Tanpa akun', detail: 'Langsung pakai' },
              { ikon: 'shield-check', judul: 'Tidak disimpan', detail: 'Diproses di memori server' },
              { ikon: 'zap', judul: 'Tanpa antrean', detail: 'Hasil langsung jadi' },
              { ikon: 'wallet', judul: 'Tanpa biaya', detail: 'Sekarang & seterusnya' }
            ]"
          />
        </div>
      </div>
    </section>

    <!-- PRESET -->
    <section class="ca-tunda py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganJudulBagian
          badge="Preset siap pakai"
          badge-ikon="target"
          judul="Target ukuran yang sering diminta"
        />

        <div class="mt-8 grid gap-4 sm:grid-cols-3">
          <NuxtLink v-for="p in preset" :key="p.ke" :to="p.ke" class="ca-focus rounded-card">
            <CaCard interaktif class="h-full">
              <p class="ca-num ca-h2 text-brand leading-none">{{ p.label }}</p>
              <p class="ca-caption text-muted mt-3 leading-relaxed">{{ p.konteks }}</p>
              <p class="ca-caption text-brand font-semibold mt-4 inline-flex items-center gap-1">
                Buka halamannya
                <RinganIkon nama="arrow-right" class="size-3.5" />
              </p>
            </CaCard>
          </NuxtLink>
        </div>
      </div>
    </section>

    <!-- URAIAN -->
    <section class="ca-tunda py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganJudulBagian
          badge="Sebelum mengompres"
          badge-ikon="search"
          judul="Periksa dulu asal berkasnya"
        />

        <CaProsa class="mt-6 max-w-3xl">
          <p>
            Ada dua jenis PDF yang perlakuannya sangat berbeda. PDF yang dibuat dari dokumen ketik —
            surat lamaran yang dicetak ke PDF dari pengolah kata, misalnya — isinya teks sungguhan
            dan biasanya sudah kecil sejak awal. PDF hasil pindai isinya gambar per halaman, dan
            ukurannya ditentukan oleh resolusi pindaian.
          </p>
          <p>
            Bila berkas kamu jenis kedua dan tetap terlalu besar setelah dikompres, cara paling
            ampuh bukan mengompres berulang kali melainkan memindai ulang pada resolusi 200 sampai
            300 dpi, dalam mode abu-abu bila dokumennya memang tidak berwarna. Selisihnya bisa
            mencapai sepuluh kali lipat, dan keterbacaannya justru lebih terjaga dibanding memaksa
            kompresi berlebihan.
          </p>
        </CaProsa>

        <RinganPesan tone="warn" title="Jangan proses ulang PDF yang sudah ber-e-meterai" class="mt-6 max-w-3xl">
          Surat lamaran dan surat pernyataan dalam pendaftaran SSCASN termasuk kategori ini.
          Menyusun ulang isi berkasnya berisiko merusak validitas meterai elektronik, dan berkas
          yang meterainya rusak akan ditolak meski ukurannya sudah pas.
        </RinganPesan>
      </div>
    </section>

    <!-- AJAKAN BERKASKILAT + KOMPRES BANYAK -->
    <section class="ca-tunda pb-12 sm:pb-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6 flex flex-col gap-4">
        <RinganAjakanKompresBanyak />
        <RinganAjakanKilat
          konteks="Ijazah 800 KB, transkrip 500 KB, akta 500 KB, pas foto 200 KB — tiap dokumen punya batasnya sendiri dan harus diproses satu per satu."
        />
      </div>
    </section>

    <!-- FAQ -->
    <section class="ca-tunda py-12 sm:py-16 bg-page-subtle border-y border-line">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganFaq :daftar="faq" badge="Sebelum mengunggah" />
      </div>
    </section>

    <!-- ALAT LAIN -->
    <section class="ca-tunda py-12 sm:py-16">
      <div class="mx-auto w-full max-w-[var(--container-ca)] px-4 sm:px-6">
        <RinganJudulBagian :level="3" judul="Alat lain" />
        <div class="mt-5 flex flex-col sm:flex-row gap-3">
          <RinganTombol variant="secondary" size="lg" icon="image" to="/kompres-foto">
            Kompres foto ke ukuran KB
          </RinganTombol>
          <RinganTombol variant="secondary" size="lg" icon="contact" to="/rapikontak">
            RapiKontak
          </RinganTombol>
        </div>
      </div>
    </section>
  </div>
</template>
